import { IOrganization, IScopeClause } from "@/api";
import { LinkRouter } from "@/components/shared/buttons/LinkRouter";
import { DEFAULT_GUIDANCE_LABEL } from "@/config/constants";
import { formatDate } from "@/utils";
import { inheritedGuidanceLabel } from "@/utils/inheritedGuidanceLabel";
import { resolveClauseGuidanceWithSource } from "@/utils/resolveClauseGuidance";
import { Typography } from "@mui/material";

/**
 * A typography component that renders the annotation for the clause guidance
 */
const AnnotationTypography = ({ children }: { children: React.ReactNode }) => (
    <Typography
        variant="caption"
        color="textSecondary"
        sx={{ mt: 1, fontStyle: "italic", overflowWrap: "anywhere" }}
    >
        {children}
    </Typography>
);

interface IProps {
    /**
     * The scope clause to render the annotation for
     */
    scopeClause: IScopeClause;
    /**
     * The organization to render the annotation for
     */
    organization: IOrganization;
}

/**
 * Renders the annotation for the clause guidance
 */
export const ClauseGuidanceAnnotation = ({
    scopeClause,
    organization,
}: IProps) => {
    const resolved = resolveClauseGuidanceWithSource(scopeClause);

    switch (resolved.source) {
        case "scope": {
            const annotation = `Guidance written for this scope ${
                scopeClause.guidance_created_by
                    ? `by ${scopeClause.guidance_created_by.first_name} ${scopeClause.guidance_created_by.last_name}`
                    : ""
            } on ${formatDate(scopeClause.updated_at, "PP")}`;
            return <AnnotationTypography>{annotation}</AnnotationTypography>;
        }
        case "organization": {
            const annotationLink = `${organization?.name} Clause Library`;
            const linkUrl = `/organizations/clause-library?clauseId=${scopeClause.clause.id}`;

            return (
                <AnnotationTypography>
                    Guidance for the{" "}
                    <LinkRouter to={linkUrl} target="_blank">
                        {annotationLink}
                    </LinkRouter>
                </AnnotationTypography>
            );
        }
        case "inherited_from_group":
            return (
                <AnnotationTypography>
                    {inheritedGuidanceLabel(resolved.sharedByOrganizationName)}
                </AnnotationTypography>
            );
        case "default":
            return (
                <AnnotationTypography>
                    {DEFAULT_GUIDANCE_LABEL}
                </AnnotationTypography>
            );
        default:
            resolved satisfies never;
            return null;
    }
};
